import { useCallback } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import {
  GRID_URL_KEYS, gridSearchParams,
  type GridUrl,
} from '@solum/experience'

export {
  GRID_URL_KEYS, canonicalGridUrl, decodeFilterParam, decodeSortParam,
  encodeFilterParam, encodeSortParam, gridSearchParams, sameGridUrl, withGridSearch,
} from '@solum/experience'
export type { GridUrl, SortTerm } from '@solum/experience'

/**
 * A ROOT grid is URL-addressable (#2375 §11): `?view=&q=&sort=name,-kind&f=<encoded>`.
 *
 * Only depth 0 — a nested grid's state is per tab and per path, for the session
 * (`relationCollection.tsx`), and never reaches the address bar.
 *
 * The URL is the grid's state, not a copy of it: a list route opens from it, a
 * change writes it canonically (never dropping the route's own keys such as
 * `peek`), Back and Forward restore it, and clearing a state removes its key.
 * A value equal to the saved view's own is NOT written, so an untouched list
 * has a clean address.
 */
/**
 * Read and write the grid keys of the CURRENT route's search, keeping every
 * other key. `push` makes a history entry (view, sort, filter), so Back
 * restores it; `replace` is for typing a search.
 */
export function useGridUrl(enabled: boolean): { url: GridUrl; write: (next: GridUrl, mode: 'push' | 'replace') => void } {
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const navigate = useNavigate()
  const url = enabled ? gridSearchParams(search) : {}
  const write = useCallback((next: GridUrl, mode: 'push' | 'replace') => {
    if (!enabled) return
    void navigate({
      to: '.',
      replace: mode === 'replace',
      search: ((prev: Record<string, unknown>) => {
        const kept = Object.fromEntries(Object.entries(prev).filter(([key]) => !(GRID_URL_KEYS as readonly string[]).includes(key)))
        return { ...kept, ...next }
      }) as never,
    })
  }, [enabled, navigate])
  return { url, write }
}
